export const isBoolean = (data: unknown): data is boolean =>
  typeof data === 'boolean'

export const isNumber = (data: unknown): data is number =>
  typeof data === 'number'

export const isString = (data: unknown): data is string =>
  typeof data === 'string'

export const isArray = (value: unknown): value is unknown[] =>
  Array.isArray(value)

export const isUnknown = (d: unknown): d is unknown => true

/**
 * Use this, but keep {@link hasObjectConstructor} for backward compatibility
 * @param data
 */
export const isObject = (data: unknown): data is object =>
  typeof data === 'object' && data !== null

/**
 * @deprecated Use `isRealObject` instead. This implementation cannot be used to narrow down the type.
 */
export const hasObjectConstructor = (
  data: unknown,
): data is { constructor: ObjectConstructor } | Record<string, unknown> =>
  isObject(data) && Object.getPrototypeOf(data)?.constructor === Object

export const isNull = (data: unknown): data is null => data === null

export const isDatetime = (data: unknown): data is string => {
  const dateTimeRegex = new RegExp(
    /^\d{4}(-|\/)\d{2}(-|\/)\d{2}(?: \d{2}:\d{2})?$/,
  )
  return isString(data) && dateTimeRegex.test(data)
}

export const isDate = (data: unknown): data is Date =>
  data instanceof Date && !isNaN(data.getTime())

// TODO move to RichTextContent module, and fix the type errors!
export const isRichtext = (data: unknown): boolean => {
  return (
    hasObjectConstructor(data) && data?.type === 'doc' && isArray(data?.content)
  )
}

export const isUndefined = (data: unknown): data is undefined =>
  typeof data === 'undefined'

export const isFunction = (
  data: unknown,
): data is (...args: unknown[]) => unknown => typeof data === 'function'

export const hasKey = <K extends string | number>(
  data: object,
  key: K,
): data is { [k in K]: unknown } => key in data

export const hasProp = <K extends string | number, T>(
  data: object,
  key: K,
  isType: (content: unknown) => content is T,
): data is { [k in K]: T } => hasKey(data, key) && isType(data[key])

export const hasOptionalProp = <K extends string | number, T>(
  data: object,
  key: K,
  isType: (content: unknown) => content is T,
): data is { [k in K]?: T } =>
  !hasKey(data, key) || isUndefined(data[key]) || isType(data[key])

export const isRecord =
  <Value>(isType: (data: unknown) => data is Value) =>
  (data: unknown): data is Record<string, Value> =>
    isObject(data) &&
    !isArray(data) &&
    Object.keys(data).every(isString) &&
    Object.values(data).every(isType)

/**
 * Type guard to check if a value is a non-null object
 * with string keys and unknown values.
 *
 * @param v - The value to check.
 * @returns True if `v` is an object and not null.
 */
export const isObjectRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null
